
import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnInit, OnDestroy, PLATFORM_ID, ViewChild, AfterViewInit } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { Subscription } from 'rxjs';

Chart.register(...registerables);

@Component({
  selector: 'app-debt-chart',
  templateUrl: './debt-chart.component.html',
  styleUrls: ['./debt-chart.component.scss'],
  standalone: false
})
export class DebtChartComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('debtChart', { static: false }) debtChart!: ElementRef<HTMLCanvasElement>;

  @Input() set gauge(gaugeValue: any) {
    if (gaugeValue !== undefined && gaugeValue !== null) {
      this.value = parseFloat(gaugeValue).toFixed(2);
      this.requestUpdate();
    }
  }

  @Input() set status(statusVal: any) {
    if (statusVal) {
      this.type = statusVal;
      this.requestUpdate();
    }
  }

  value: string | null = null;
  type = "";
  private chart: any;
  private isBrowser: boolean;
  private observerSubscription: Subscription | undefined;
  private updateQueued = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private observer: BreakpointObserver
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.observerSubscription = this.observer.observe('(max-width: 768px)').subscribe(() => {
        this.requestUpdate();
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.requestUpdate();
    }
  }

  ngOnDestroy(): void {
    if (this.observerSubscription) {
      this.observerSubscription.unsubscribe();
    }
    if (this.chart) {
      this.chart.destroy();
    }
  }

  /**
   * Batches multiple update requests into a single render to prevent flickering.
   */
  private requestUpdate() {
    if (this.updateQueued || !this.isBrowser) return;

    this.updateQueued = true;
    // Use a microtask to schedule the render after all inputs/lifecycle events in this tick
    Promise.resolve().then(() => {
      this.updateQueued = false;
      this.renderChart();
    });
  }

  private renderChart() {
    if (!this.debtChart?.nativeElement || this.value === null) {
      return;
    }

    const canvas = this.debtChart.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (this.chart) {
      this.chart.destroy();
    }

    const needleVal = parseFloat(this.value);
    const displayVal = this.value;

    const gaugeNeedle = {
      id: 'gaugeNeedle',
      afterDatasetsDraw(chart: any) {
        const { ctx, data } = chart;
        const meta = chart.getDatasetMeta(0);
        if (!meta.data || !meta.data[0]) return;

        const xCenter = meta.data[0].x;
        const yCenter = meta.data[0].y;
        const outerRadius = meta.data[0].outerRadius;
        
        ctx.save();
        
        const needleValue = data.datasets[0].needleValue;
        const angle = ((needleValue / 100) * Math.PI) + Math.PI;
        
        ctx.translate(xCenter, yCenter);
        ctx.rotate(angle);
        
        // Needle
        ctx.beginPath();
        ctx.moveTo(0, -2);
        ctx.lineTo(outerRadius - 10, 0);
        ctx.lineTo(0, 2);
        ctx.fillStyle = '#212121';
        ctx.fill();
        
        // Pivot
        ctx.beginPath();
        ctx.arc(0, 0, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#212121';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        ctx.restore();

        // Label
        ctx.save();
        const fontSize = chart.width < 350 ? 20 : 32;
        ctx.font = `600 ${fontSize}px "Poppins", sans-serif`;
        ctx.fillStyle = '#212121';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(displayVal + '%', xCenter, yCenter - 5);
        ctx.restore();
      }
    };

    this.chart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ["Compliant", "Non-Compliant"],
        datasets: [{
          data: [30, 70],
          backgroundColor: ['#F6D8D8', '#D43A3A'],
          borderWidth: 2,
          borderColor: '#ffffff',
          needleValue: needleVal > 100 ? 100 : (needleVal < 0 ? 0 : needleVal)
        }] as any
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        cutout: '60%',
        rotation: -90,
        circumference: 180,
        animation: {
          duration: 400 // Smooth but fast animation
        },
        layout: {
          padding: { bottom: 20 }
        },
        plugins: {
          tooltip: { enabled: false },
          legend: { display: false }
        }
      },
      plugins: [gaugeNeedle]
    });
  }
}
